import React from 'react';
import { Lock, ArrowRight } from 'lucide-react';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import { CONTENT_WIDTH, PAGE_STACK, TEXT } from '../../uiTokens';

const STEPS = [
  { label: 'Paste', detail: 'Type a secret, API key, or note in your browser.' },
  { label: 'Encrypt', detail: 'Your browser encrypts it. We only ever store the encrypted version.' },
  { label: 'Share', detail: 'Send the one-time link. The key travels in the part after #, which never reaches us.' },
  { label: 'Reveal once', detail: 'The recipient opens it once. Then the link is dead.' },
];

const PRINCIPLES = [
  {
    title: 'Encrypted before upload',
    body: 'AES-256-GCM runs in the browser. The server, the CDN, and our team only see ciphertext.',
  },
  {
    title: 'Nothing to clean up',
    body: 'Chat and email keep forever copies. A one-time link deletes itself the moment it is read, or when it expires.',
  },
  {
    title: 'Honest about scope',
    body: 'Slack and Discord commands encrypt on the server when the link is created. The web app never sends plaintext.',
  },
];

export default function AboutPage({ onNavigateHome }) {
  return (
    <div className={`${CONTENT_WIDTH} ${PAGE_STACK}`}>
      <PageHeader
        eyebrow="About"
        title="Hand someone a secret. Leave no copy behind."
        description="Hushify turns sensitive text into a link that works one time. Encryption happens in your browser first, so the secret is gone the moment it is revealed."
      />

      <section className="mt-14 sm:mt-20" aria-labelledby="how-it-works">
        <div className="flex items-baseline justify-between gap-4 mb-6">
          <h2 id="how-it-works" className={TEXT.h2}>How it works</h2>
          <span className={TEXT.caption}>Four steps, under a minute</span>
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8 border-t border-line pt-6">
          {STEPS.map(({ label, detail }, index) => (
            <li key={label} className="flex flex-col gap-3">
              <span className="font-mono text-xs tabular-nums text-accent">0{index + 1}</span>
              <div>
                <h3 className={TEXT.h3}>{label}</h3>
                <p className={`${TEXT.small} mt-1.5 leading-relaxed`}>{detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-16 sm:mt-24 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-6 md:gap-16" aria-labelledby="why">
        <div>
          <h2 id="why" className={TEXT.h2}>Why one-time links</h2>
          <p className={`${TEXT.small} mt-3 max-w-[36ch]`}>
            Use it for a temporary secret, an on-call credential, or a key a vendor needs today.
          </p>
        </div>
        <ul className="flex flex-col divide-y divide-line">
          {PRINCIPLES.map(({ title, body }) => (
            <li key={title} className="py-5 first:pt-0 last:pb-0">
              <h3 className={TEXT.h3}>{title}</h3>
              <p className={`${TEXT.body} mt-1.5`}>{body}</p>
            </li>
          ))}
        </ul>
      </section>

      {onNavigateHome && (
        <section className="mt-16 sm:mt-24 rule-fade-t pt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <p className={TEXT.h3}>Ready when you are.</p>
            <p className={`${TEXT.small} mt-1`}>No account. Nothing is uploaded until you create the link.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button size="lg" onClick={onNavigateHome}>
              <Lock className="h-4 w-4" aria-hidden="true" />
              Share a secret
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
